export { CorruptionReporterService } from '../corruption-reporter';
export type { MemberQuotaInfo, SeatAddonInfo } from './entities/seat-addon';
export { Server } from './entities/server';
export type { AuthAccountInfo } from './entities/session';
export { AccountChanged } from './events/account-changed';
export { AccountLoggedIn } from './events/account-logged-in';
export { AccountLoggedOut } from './events/account-logged-out';
export { useCachedOwnAvatarUrl } from './hooks/use-cached-own-avatar-url';
export { useCachedUserAvatarUrl } from './hooks/use-cached-user-avatar-url';
export { isLocalWorkspaceEnabled } from './local-workspace-policy';
export { AuthProvider } from './provider/auth';
export { ValidatorProvider } from './provider/validator';
export { ServerScope } from './scopes/server';
export { AccessTokenService } from './services/access-token';
export { type AccessGateMode, AuthService } from './services/auth';
export {
  type AvatarAssetCacheInput,
  AvatarAssetCacheService,
  toAbsoluteAvatarAssetUrl,
  USER_AVATAR_CACHE_LIMIT,
  WORKSPACE_LOGO_CACHE_LIMIT,
} from './services/avatar-asset-cache';
export { CaptchaService } from './services/captcha';
export { DefaultServerService } from './services/default-server';
export { DocCreatedByUpdatedBySyncService } from './services/doc-created-by-updated-by-sync';
export { EventSourceService } from './services/eventsource';
export { FetchService } from './services/fetch';
export { GraphQLService } from './services/graphql';
export { InvitationService } from './services/invitation';
export type { PublicUserInfo } from './services/public-user';
export { PublicUserService } from './services/public-user';
export {
  defaultRequestCoordinator,
  RequestCoordinator,
  type RequestCoordinatorPort,
  type WorkspaceRequestCategory,
  type WorkspaceRequestDescriptor,
} from './services/request-coordinator';
export {
  type BackendDegradationScope,
  defaultScopedDegradationStore,
  type ScopedDegradationState,
  ScopedDegradationStore,
} from './services/scoped-degradation-store';
export { SeatAddonService } from './services/seat-addon';
export { ServerService } from './services/server';
export { ServersService } from './services/servers';
export { SubscriptionService } from './services/subscription';
export { UserApiKeysModeService } from './services/user-api-keys-mode';
export { UserCopilotQuotaService } from './services/user-copilot-quota';
export { UserFeatureService } from './services/user-feature';
export { UserQuotaService } from './services/user-quota';
export {
  type UserSettings,
  UserSettingsService,
} from './services/user-settings';
export {
  defaultWorkspaceApiClient,
  WorkspaceApiClient,
  WorkspaceApiError,
  workspaceApiFetch,
  type WorkspaceApiGraphQLRequest,
} from './services/workspace-api-client';
export { WorkspaceInvoicesService } from './services/workspace-invoices';
export {
  invalidateWorkspaceRead,
  readWorkspaceCached,
} from './services/workspace-read-cache';
export { WorkspaceServerService } from './services/workspace-server';
export { WorkspaceSubscriptionService } from './services/workspace-subscription';
export type { ServerConfig } from './types';
export { isWebAuthnUserCancel } from './utils/webauthn-errors';

// eslint-disable-next-line simple-import-sort/imports
import { type Framework } from '@toeverything/infra';

import {
  BrowserEnvironmentDetector,
  CorruptionReporterService,
  LocalStorageOfflineQueue,
} from '../corruption-reporter';
import {
  CacheStorage,
  GlobalCache,
  GlobalState,
} from '../storage/providers/global';
import { GlobalStateService } from '../storage/services/global';
import { UrlService } from '../url';
import { WorkspaceScope, WorkspaceService } from '../workspace';
import { configureWorkspaceBlobFetchFactory } from '../workspace/utils/blob-cache';
import { CloudDocMeta } from './entities/cloud-doc-meta';
import { Server } from './entities/server';
import { AuthSession } from './entities/session';
import { Subscription } from './entities/subscription';
import { PlanRoles } from './entities/plan-roles';
import { SubscriptionPrices } from './entities/subscription-prices';
import { UserApiKeysMode } from './entities/user-api-keys-mode';
import { UserCopilotQuota } from './entities/user-copilot-quota';
import { UserFeature } from './entities/user-feature';
import { UserQuota } from './entities/user-quota';
import { WorkspaceInvoices } from './entities/workspace-invoices';
import { WorkspaceSubscription } from './entities/workspace-subscription';
import { SeatAddon } from './entities/seat-addon';
import { configureDefaultAuthProvider } from './impl/auth';
import { AuthProvider } from './provider/auth';
import { ServerScope } from './scopes/server';
import { InvitationService } from './services/invitation';
import { AuthService } from './services/auth';
import { BlocksuiteWriterInfoService } from './services/blocksuite-writer-info';
import { CaptchaService } from './services/captcha';
import { CloudDocMetaService } from './services/cloud-doc-meta';
import { DefaultServerService } from './services/default-server';
import { EventSourceService } from './services/eventsource';
import { FetchService } from './services/fetch';
import { GraphQLService } from './services/graphql';
import { PublicUserService } from './services/public-user';
import { ServerService } from './services/server';
import { ServersService } from './services/servers';
import { SubscriptionService } from './services/subscription';
import { UserApiKeysModeService } from './services/user-api-keys-mode';
import { UserCopilotQuotaService } from './services/user-copilot-quota';
import { UserFeatureService } from './services/user-feature';
import { UserQuotaService } from './services/user-quota';
import { UserSettingsService } from './services/user-settings';
import { WorkspaceInvoicesService } from './services/workspace-invoices';
import { workspaceApiFetch } from './services/workspace-api-client';
import { WorkspaceServerService } from './services/workspace-server';
import { WorkspaceSubscriptionService } from './services/workspace-subscription';
import { SeatAddonService } from './services/seat-addon';
import { AcceptInviteStore } from './stores/accept-invite';
import { AuthStore } from './stores/auth';
import { CloudDocMetaStore } from './stores/cloud-doc-meta';
import { InviteInfoStore } from './stores/invite-info';
import { InvoicesStore } from './stores/invoices';
import { PublicUserStore } from './stores/public-user';
import { ServerConfigStore } from './stores/server-config';
import { ServerListStore } from './stores/server-list';
import { SubscriptionStore } from './stores/subscription';
import { SeatAddonStore } from './stores/seat-addon';
import { UserApiKeysModeStore } from './stores/user-api-keys-mode';
import { UserCopilotQuotaStore } from './stores/user-copilot-quota';
import { UserFeatureStore } from './stores/user-feature';
import { UserQuotaStore } from './stores/user-quota';
import { UserSettingsStore } from './stores/user-settings';
import { DocCreatedByService } from './services/doc-created-by';
import { DocUpdatedByService } from './services/doc-updated-by';
import { DocCreatedByUpdatedBySyncService } from './services/doc-created-by-updated-by-sync';
import { WorkspacePermissionService } from '../permissions';
import { DocScope, DocService, DocsService } from '../doc';
import { DocCreatedByUpdatedBySyncStore } from './stores/doc-created-by-updated-by-sync';
import { AccessTokenService } from './services/access-token';
import { AccessTokenStore } from './stores/access-token';
import { AvatarAssetCacheService } from './services/avatar-asset-cache';

export function configureCloudModule(framework: Framework) {
  configureDefaultAuthProvider(framework);
  configureWorkspaceBlobFetchFactory(
    workspaceId => (input, init) =>
      workspaceApiFetch(input, init, {
        workspaceId,
        category: 'workspace-api',
        key: `workspace-blob:${init?.method ?? 'GET'}:${String(input)}`,
        mode: 'background',
        priority: 'visible',
      })
  );

  framework
    .service(ServersService, [ServerListStore, ServerConfigStore])
    .service(DefaultServerService, [ServersService])
    .store(ServerListStore, [GlobalStateService])
    .store(ServerConfigStore)
    .entity(Server, [ServerListStore])
    .scope(ServerScope)
    .service(ServerService, [ServerScope])
    .service(FetchService, [ServerService])
    .service(EventSourceService, [ServerService])
    .service(GraphQLService, [FetchService])
    .service(AvatarAssetCacheService, [CacheStorage])
    .service(CaptchaService, [FetchService])
    .service(AuthService, [FetchService, AuthStore, UrlService])
    .store(AuthStore, [
      FetchService,
      GraphQLService,
      GlobalState,
      ServerService,
      AuthProvider,
    ])
    .entity(AuthSession, [AuthStore])
    .service(SubscriptionService, [SubscriptionStore])
    .store(SubscriptionStore, [
      GraphQLService,
      GlobalCache,
      UrlService,
      ServerService,
    ])
    .entity(Subscription, [AuthService, ServerService, SubscriptionStore])
    .entity(SubscriptionPrices, [ServerService, SubscriptionStore])
    .entity(PlanRoles, [SubscriptionStore])
    .service(UserQuotaService)
    .store(UserQuotaStore, [GraphQLService])
    .entity(UserQuota, [AuthService, UserQuotaStore])
    .service(UserCopilotQuotaService)
    .store(UserCopilotQuotaStore, [GraphQLService])
    .entity(UserCopilotQuota, [
      AuthService,
      UserCopilotQuotaStore,
      ServerService,
    ])
    .service(UserApiKeysModeService)
    .store(UserApiKeysModeStore, [FetchService])
    .entity(UserApiKeysMode, [AuthService, UserApiKeysModeStore, ServerService])
    .service(UserFeatureService)
    .entity(UserFeature, [AuthService, UserFeatureStore])
    .store(UserFeatureStore, [GraphQLService])
    .store(InvoicesStore, [GraphQLService])
    .store(InviteInfoStore, [GraphQLService])
    .service(InvitationService, [AcceptInviteStore, InviteInfoStore])
    .store(AcceptInviteStore, [GraphQLService])
    .service(PublicUserService, [PublicUserStore])
    .store(PublicUserStore, [GraphQLService])
    .service(UserSettingsService, [UserSettingsStore])
    .store(UserSettingsStore, [GraphQLService])
    .service(AccessTokenService, [AccessTokenStore])
    .store(AccessTokenStore, [GraphQLService])
    .service(LocalStorageOfflineQueue)
    .service(BrowserEnvironmentDetector)
    .service(CorruptionReporterService, [
      GraphQLService,
      LocalStorageOfflineQueue,
      BrowserEnvironmentDetector,
    ]);

  framework
    .scope(WorkspaceScope)
    .service(WorkspaceServerService)
    .service(DocCreatedByService, [WorkspaceServerService])
    .scope(DocScope)
    .service(DocUpdatedByService, [WorkspaceServerService])
    .service(CloudDocMetaService)
    .entity(CloudDocMeta, [CloudDocMetaStore, DocService, GlobalCache])
    .store(CloudDocMetaStore, [WorkspaceServerService]);
  framework
    .scope(WorkspaceScope)
    .service(WorkspaceSubscriptionService, [WorkspaceServerService])
    .entity(WorkspaceSubscription, [WorkspaceService, WorkspaceServerService])
    .service(WorkspaceInvoicesService)
    .entity(WorkspaceInvoices, [WorkspaceService, WorkspaceServerService])
    .service(BlocksuiteWriterInfoService, [WorkspaceServerService])
    .service(SeatAddonService)
    .store(SeatAddonStore, [WorkspaceServerService])
    .entity(SeatAddon, [WorkspaceService, SeatAddonStore])
    .service(DocCreatedByUpdatedBySyncService, [
      WorkspaceService,
      DocsService,
      WorkspacePermissionService,
      DocCreatedByUpdatedBySyncStore,
    ])
    .store(DocCreatedByUpdatedBySyncStore, [
      WorkspaceServerService,
      WorkspaceService,
    ]);
}
